<template>
  <div>
    <!-- دکمه فعال‌ساز -->
    <v-btn
      class="btn-download rounded-lg"
      @click="dialog = true"
      :disabled="disabled"
    >
      <v-icon right class="px-2">mdi-export</v-icon>
      <span>خروجی گرفتن</span>
    </v-btn>

    <custom-popup
      v-model="dialog"
      :cons="{
        title: 'تنظیمات خروجی نهایی',
        icon: 'mdi-export',
      }"
      max-width="500"
      hide-confirm
      :reloadable="false"
      :persistent="true"
    >
      <div class="pa-4">
        <v-select
          v-model="exportSettings.format"
          :items="[
            { text: 'PNG (پس‌زمینه شفاف)', value: 'image/png' },
            { text: 'JPEG (برای چاپ)', value: 'image/jpeg' },
            { text: 'WEBP (حجم کمتر)', value: 'image/webp' },
            { text: 'PSD (لایه باز - فتوشاپ)', value: 'psd' },
            { text: 'DXF (طرح برداری AutoCAD)', value: 'dxf' }
          ]"
          label="فرمت فایل خروجی"
          outlined
          dense
        ></v-select>

        <!-- اسلایدر کیفیت -->
        <v-slider
          v-if="exportSettings.format !== 'image/png' && exportSettings.format !== 'psd' && exportSettings.format !== 'dxf'"
          v-model="exportSettings.quality"
          label="کیفیت خروجی"
          min="0.1"
          max="1"
          step="0.05"
          class="mt-3"
        >
          <template v-slot:append>
            <v-text-field
              v-model="exportSettings.quality"
              class="mt-0 pt-0"
              hide-details
              single-line
              outlined
              dense
              type="number"
              step="0.05"
              min="0.1"
              max="1"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>

        <!-- اسلایدر رزولوشن -->
        <v-slider
          v-if="exportSettings.format !== 'dxf'"
          v-model="exportSettings.scale"
          label="ضریب رزولوشن (Scale)"
          min="1"
          max="5"
          step="1"
          ticks
          hint="عدد بزرگتر = عکس باکیفیت‌تر اما حجم فایل بیشتر"
          persistent-hint
          class="mt-3"
        >
          <template v-slot:append>
            <v-text-field
              v-model="exportSettings.scale"
              class="mt-0 pt-0"
              hide-details
              single-line
              outlined
              dense
              type="number"
              step="1"
              min="1"
              max="5"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>

        <div class="d-flex justify-space-between mt-6">
          <v-btn class="btn-delete rounded-lg" text @click="dialog = false">
            <v-icon>mdi-close</v-icon>
            انصراف
          </v-btn>
          <v-btn
            class="btn-get-code rounded-lg"
            :loading="isExporting"
            @click="downloadFinalLayout"
            ><v-icon>mdi-download</v-icon>
            دانلود فایل
          </v-btn>
        </div>
      </div>
    </custom-popup>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import { writePsd } from "ag-psd";
import { saveAs } from "file-saver";
import Drawing from "dxf-writer"; 

export default {
  name: "LayoutExportModal",
  props: {
    targetId: {
      type: String,
      required: true,
    },
    isTransparent: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // مختصات کیوآرکد (برای خروجی DXF)
    qrConfig: {
      type: Object,
      default: () => ({ x: 0, y: 0, size: 100 }), 
    },
    // مختصات متن/سریال (برای خروجی DXF)
    labelConfig: {
      type: Object,
      default: () => ({ text: "", x: 0, y: 0, fontSize: 14 }),
    },
    // ابعاد کل قالب برای معکوس کردن محور Y در اتوکد
    layoutDimensions: {
      type: Object,
      default: () => ({ width: 500, height: 500 }),
    }
  },
  data() {
    return {
      dialog: false,
      isExporting: false,
      exportSettings: {
        format: "image/jpeg",
        quality: 0.9,
        scale: 3,
      },
    };
  },
  methods: {
    async downloadFinalLayout() {
      this.isExporting = true;
      try {
        const timestamp = new Date().getTime();
        const layoutElement = document.getElementById(this.targetId);
        
        if (!layoutElement) {
          throw new Error("المان مورد نظر برای خروجی پیدا نشد.");
        }

        // تولید Canvas اولیه (برای تمام فرمت‌ها نیاز است تا محتوا دقیق رندر شود)
        const finalBgColor =
          this.exportSettings.format === "image/jpeg" || !this.isTransparent
            ? "#ffffff"
            : null;

        const canvas = await html2canvas(layoutElement, {
          useCORS: true,
          allowTaint: false,
          scale: this.exportSettings.scale,
          backgroundColor: finalBgColor,
        });

        // ---------------- خروجی DXF ----------------
        if (this.exportSettings.format === "dxf") {
          let d = new Drawing();
          d.setUnits('Pixels');
          
          const ctx = canvas.getContext("2d");
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imgData.data;

          const step = 2; // تنظیم قدم اسکن برای تعادل کیفیت و حجم خروجی

          // اسکن پیکسلی و تبدیل پیکسل‌های تیره به مستطیل‌های برداری
          for (let y = 0; y < canvas.height; y += step) {
            for (let x = 0; x < canvas.width; x += step) {
              const index = (y * canvas.width + x) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              const a = data[index + 3];

              // بررسی تیرگی پیکسل (آستانه ۱۲۸)
              if (a > 128 && (r + g + b) / 3 < 128) {
                // محور Y در اتوکد برعکس است، بنابراین Y را منفی می‌دهیم
                d.drawRect(x, -y, x + step, -(y + step));
              }
            }
          }

          const dxfString = d.toDxfString();
          const blob = new Blob([dxfString], { type: "application/dxf" });
          saveAs(blob, `QR-Layout-${timestamp}.dxf`);
          
          this.dialog = false;
          this.$toast.success("خروجی DXF با موفقیت ایجاد شد.");
          this.isExporting = false;
          return;
        }

        // ---------------- خروجی‌های تصویری و PSD ----------------
        if (this.exportSettings.format === "psd") {
          const psdData = {
            width: canvas.width,
            height: canvas.height,
            children: [
              {
                name: "QR Layout Layer",
                canvas: canvas,
              },
            ],
          };

          const buffer = writePsd(psdData);
          const blob = new Blob([buffer], { type: "application/octet-stream" });
          saveAs(blob, `QR-Layout-${timestamp}.psd`);
        } else {
          const dataUrl = canvas.toDataURL(
            this.exportSettings.format,
            this.exportSettings.format === "image/png"
              ? undefined
              : this.exportSettings.quality
          );

          const extension = this.exportSettings.format.split("/")[1];

          const link = document.createElement("a");
          link.download = `QR-Layout-${timestamp}.${extension}`;
          link.href = dataUrl;
          link.click();
        }

        this.dialog = false;
        this.$toast.success("خروجی با موفقیت ایجاد شد.");
      } catch (error) {
        console.error("خطا در تولید فایل خروجی:", error);
        this.$toast.error("مشکلی در تولید فایل خروجی به وجود آمد.");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>
